import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  FileCheck, RefreshCw, Loader2, Search, ExternalLink, MapPin, List, Map as MapIcon,
} from "lucide-react";
import SwissMapView from "./SwissMapView";

const STATUT_STYLE = {
  ENREGISTREMENT: "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/30",
  "EN COURS": "bg-[#EF9F27]/15 text-[#EF9F27] border-[#EF9F27]/30",
  ACCEPTE: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  REFUSE: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function PermitsTab() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState({ canton: "", statut: "", type_dossier: "", commune: "", q: "", days: 90 });
  const [view, setView] = useState("list");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 150, days: filter.days };
      if (filter.canton) params.canton = filter.canton;
      if (filter.statut) params.statut = filter.statut;
      if (filter.type_dossier) params.type_dossier = filter.type_dossier;
      if (filter.commune) params.commune = filter.commune;
      if (filter.q) params.q = filter.q;
      const [s, e] = await Promise.all([
        api.get("/workspace/realestate/permits/summary"),
        api.get("/workspace/realestate/permits", { params }),
      ]);
      setSummary(s.data);
      setItems(e.data?.items || []);
    } catch (err) {
      notify.error(fmtError(err, "Chargement des permis impossible."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.canton, filter.statut, filter.type_dossier, filter.commune, filter.days]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/workspace/realestate/permits/refresh");
      if (r.data?.started) {
        notify.success(r.data.message || "Scan lancé en arrière-plan.");
        setTimeout(load, 45000);
      } else {
        notify.success(`Scan terminé — ${r.data?.inserted ?? 0} nouveaux dossiers.`);
        await load();
      }
    } catch (err) {
      notify.error(fmtError(err, "Scan impossible."));
    } finally { setRefreshing(false); }
  };

  const kanton = (c) => ({ GE: "#378ADD", VD: "#1D9E75", FR: "#A78BFA", NE: "#F4D35E" }[c] || "#71717A");

  return (
    <div className="space-y-4" data-testid="immo-permits-tab">
      <div className="noam-card p-4 border-[#378ADD]/20 bg-[#378ADD]/[0.03]">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#378ADD]">
          <FileCheck className="w-3.5 h-3.5" /> Autorisations de construire — Genève · Vaud · Fribourg · Neuchâtel
        </div>
        <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed max-w-3xl">
          Dossiers déposés auprès des cantons (sources officielles SITG, ACTIS/CAMAC, FRIAC, SATAC — open data),
          actualisés chaque jour : <strong className="text-zinc-200">qui construit, où, quand, à quel stade</strong>.
          Un permis déposé = un futur besoin de financement, d'assurance ou de courtage.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setFilter((f) => ({ ...f, canton: "" }))}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!filter.canton ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
            data-testid="permits-canton-all"
          >
            Tous cantons ({summary?.total ?? 0})
          </button>
          {(summary?.cantons || []).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter((f) => ({ ...f, canton: f.canton === c ? "" : c }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter.canton === c ? "bg-[#EF9F27]/15 text-[#EF9F27] border-[#EF9F27]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
              data-testid={`permits-canton-${c}`}
            >
              {c} ({summary?.by_canton?.[c] ?? 0})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-white/10 overflow-hidden">
            <button
              type="button"
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-[#378ADD]/15 text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid="permits-view-list"
            >
              <List className="w-3.5 h-3.5" /> Liste
            </button>
            <button
              type="button"
              onClick={() => setView("map")}
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "map" ? "bg-[#378ADD]/15 text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid="permits-view-map"
            >
              <MapIcon className="w-3.5 h-3.5" /> Carte
            </button>
          </div>
          <button type="button" onClick={refresh} disabled={refreshing} className="btn-primary text-xs disabled:opacity-50" data-testid="permits-refresh-btn">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {refreshing ? "Scan…" : "Scanner maintenant"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher (villa, surélévation, panneaux…)"
            className="noam-input pl-8 text-xs h-8 w-64"
            data-testid="permits-search-input"
          />
        </div>
        <select
          value={filter.commune}
          onChange={(e) => setFilter((f) => ({ ...f, commune: e.target.value }))}
          className="noam-input text-xs h-8 w-44"
          data-testid="permits-commune-select"
        >
          <option value="">Toutes les communes</option>
          {(summary?.top_communes || []).map((c) => (
            <option key={c.commune} value={c.commune}>{c.commune} ({c.n})</option>
          ))}
        </select>
        <select
          value={filter.days}
          onChange={(e) => setFilter((f) => ({ ...f, days: Number(e.target.value) }))}
          className="noam-input text-xs h-8 w-36"
          data-testid="permits-days-select"
        >
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={365}>12 derniers mois</option>
        </select>
        {summary?.last_crawl_at && (
          <span className="text-[10px] text-zinc-500 ml-auto" data-testid="permits-last-crawl">
            Dernier scan : {new Date(summary.last_crawl_at).toLocaleString("fr-CH")}
          </span>
        )}
      </div>

      {view === "map" ? (
        <SwissMapView canton={filter.canton} days={filter.days} q={filter.q} />
      ) : loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center text-sm text-zinc-500" data-testid="permits-empty">
          <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun dossier sur la période. Lancez un scan pour alimenter le flux.
        </div>
      ) : (
        <div className="space-y-2" data-testid="permits-list">
          {items.map((it) => (
            <div key={it.dossier_id} className="noam-card p-4 hover:border-white/20 transition-colors" data-testid="permits-item">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: kanton(it.canton), background: `${kanton(it.canton)}18`, border: `1px solid ${kanton(it.canton)}40` }}>
                      {it.canton}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/5 text-zinc-300 border border-white/10" title={it.type_label}>
                      {it.dossier_id}
                    </span>
                    {it.statut && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${STATUT_STYLE[it.statut] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>
                        {it.statut}
                      </span>
                    )}
                    {it.commune && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-zinc-400 bg-white/5">
                        <MapPin className="w-2.5 h-2.5" /> {it.commune}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-100 leading-snug">
                    {it.description || it.operation || it.type_label}
                  </div>
                  {it.description && it.operation && !it.operation.startsWith("Non renseigné") && (
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed line-clamp-1">{it.operation}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">
                    {it.date_depot ? `Déposé le ${new Date(it.date_depot).toLocaleDateString("fr-CH")}` : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    {it.lat && it.lng && (
                      <a href={`https://www.google.com/maps?q=${it.lat},${it.lng}`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-zinc-200 text-[11px] inline-flex items-center gap-1" data-testid="permits-map-link">
                        <MapPin className="w-3 h-3" /> Carte
                      </a>
                    )}
                    {it.lien_sad && (
                      <a href={it.lien_sad} target="_blank" rel="noreferrer" className="text-[#EF9F27] hover:underline text-[11px] inline-flex items-center gap-1" data-testid="permits-sad-link">
                        Dossier officiel <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
